import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Activity, Clock, LogIn, Plus, Trash2, RefreshCw, BarChart3 } from 'lucide-react';
import { ACTION_ICONS, ACTION_COLORS } from './constants';

function StatCard({ icon: Icon, label, value, color, testId }) {
  const colors = {
    orange: 'bg-orange-50 text-orange-600',
    blue: 'bg-info/10 text-info',
    emerald: 'bg-success/10 text-success',
    violet: 'bg-violet-50 text-violet-600',
  };
  return (
    <Card className="border-border" data-testid={testId}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-xl font-bold text-foreground">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function ActivityLogView({ feed, stats, loading, filter, setFilter, tenants, onRefresh }) {
  return (
    <div className="space-y-6" data-testid="activity-log-view">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Activity} label="Total Actions (7d)" value={stats.total_actions || 0} color="orange" testId="stat-total-actions" />
          <StatCard icon={LogIn} label="Logins" value={stats.by_action?.login || 0} color="blue" testId="stat-logins" />
          <StatCard icon={Plus} label="Creates" value={stats.by_action?.create || 0} color="emerald" testId="stat-creates" />
          <StatCard icon={Trash2} label="Deletes" value={stats.by_action?.delete || 0} color="violet" testId="stat-deletes" />
        </div>
      )}

      {stats?.by_tenant?.length > 0 && (
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
              <BarChart3 size={14} /> Most Active Tenants (7 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.by_tenant.slice(0, 5).map((t, i) => {
                const maxCount = stats.by_tenant[0]?.count || 1;
                return (
                  <div key={i} className="flex items-center gap-3" data-testid={`active-tenant-${i}`}>
                    <span className="text-xs text-muted-foreground w-32 truncate font-medium">{t.company_name}</span>
                    <div className="flex-1 h-5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-orange-400 to-amber-400 rounded-full transition-all"
                        style={{ width: `${(t.count / maxCount) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-semibold text-foreground w-12 text-right">{t.count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
              <Clock size={14} /> Live Activity Feed
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-40 h-8 text-xs" data-testid="activity-filter">
                  <SelectValue placeholder="All tenants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs">All Tenants</SelectItem>
                  {tenants.map(t => (
                    <SelectItem key={t.id} value={t.id} className="text-xs">{t.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="sm" onClick={onRefresh} className="h-8 w-8 p-0" data-testid="refresh-activity-btn">
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : feed.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="no-activity">
              <Activity size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">No activity recorded yet</p>
              <p className="text-xs mt-1">Actions like logins, sales, expenses will appear here</p>
            </div>
          ) : (
            <div className="space-y-1 max-h-[500px] overflow-y-auto" data-testid="activity-feed-list">
              {feed.map((log, idx) => {
                const IconComp = ACTION_ICONS[log.action] || Activity;
                const colorCls = ACTION_COLORS[log.action] || ACTION_COLORS.view;
                return (
                  <div key={log.id || idx} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors" data-testid={`activity-item-${idx}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${colorCls}`}>
                      <IconComp size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground truncate">{log.user_email}</span>
                        <Badge variant="outline" className="text-[10px] capitalize shrink-0">{log.action}</Badge>
                        <Badge variant="outline" className="text-[10px] capitalize shrink-0 bg-muted/50">{log.resource}</Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground">
                        <span className="font-medium text-orange-500">{log.company_name || 'Unknown'}</span>
                        {log.ip_address && <span>&middot; {log.ip_address}</span>}
                        {log.details && Object.keys(log.details).length > 0 && (
                          <span>&middot; {Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(', ')}</span>
                        )}
                      </div>
                    </div>
                    <span className="text-[11px] text-muted-foreground shrink-0">{formatTime(log.timestamp)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
